import { createSlice } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { loginUserThunk, signupUserThunk, forgotPasswordThunk, resetPasswordThunk, getUserProfileThunk, updateUserProfileThunk, verifyEmailThunk, resendVerificationCodeThunk, firebaseLoginThunk } from './authThunks';
import { clearLocationCache } from '../../utils/locationUtils';

const initialState = {
  user: null,
  isAuthenticated: false,
  role: null, // 'user' or 'admin'
  token: null,
  loading: false,
  error: null,
  onboardingActive: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.role = null;
      state.token = null;
      state.loading = false;
      state.error = null;
      state.onboardingActive = false;
      
      // Clear location cache side effect
      clearLocationCache();
    },
    dismissOnboarding: (state) => {
      state.onboardingActive = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload.user;
      state.role = action.payload.role;
    },
    // Clear auth state
    clearAuth: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.role = null;
      state.token = null;
      state.loading = false;
      state.error = null;

      // Clear location cache side effect
      clearLocationCache();
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle rehydration from redux-persist
      .addCase(REHYDRATE, (state, action) => {
        if (action.payload && action.payload.auth) {
          const { user, isAuthenticated, role, token, onboardingActive } = action.payload.auth;
          state.user = user;
          state.isAuthenticated = isAuthenticated;
          state.role = role;
          state.token = token;
          state.onboardingActive = onboardingActive || false;
        }
      })
      // Login cases
      .addCase(loginUserThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUserThunk.fulfilled, (state, action) => {

        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.token = action.payload.token;
        state.onboardingActive = false;
        state.error = null;
      })
      .addCase(loginUserThunk.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.role = null;
        state.token = null;
        state.error = action.payload;
      })
      // Register cases
      .addCase(signupUserThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUserThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.token = action.payload.token;
        state.onboardingActive = true;
        state.error = null;
      })
      .addCase(signupUserThunk.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.role = null;
        state.token = null;
        state.error = action.payload;
      })
      // Forgot password cases
      .addCase(forgotPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(forgotPasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reset password cases
      .addCase(resetPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get user profile cases
      .addCase(getUserProfileThunk.pending, (state) => {

        state.loading = true;
        state.error = null;
      })
      .addCase(getUserProfileThunk.fulfilled, (state, action) => {

        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.error = null;
      })
      .addCase(getUserProfileThunk.rejected, (state, action) => {

        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.role = null;
        state.token = null;
        state.error = action.payload;
      })
      // Update user profile cases
      .addCase(updateUserProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Update user data with the response
        if (action.payload && state.user) {
          // Handle different response structures from the API
          const updatedUserData = action.payload.user || action.payload;
          state.user = { ...state.user, ...updatedUserData };
        }
      })
      .addCase(updateUserProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify email cases
      .addCase(verifyEmailThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmailThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.token = action.payload.token;
        state.onboardingActive = true;
        state.error = null;
      })
      .addCase(verifyEmailThunk.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.role = null;
        state.token = null;
        state.error = action.payload;
      })
      // Resend verification code cases
      .addCase(resendVerificationCodeThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendVerificationCodeThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resendVerificationCodeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Firebase login cases
      .addCase(firebaseLoginThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(firebaseLoginThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.token = action.payload.token;
        state.onboardingActive = action.payload.isNewUser || false;
        state.error = null;
      })
      .addCase(firebaseLoginThunk.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.role = null;
        state.token = null;
        state.error = action.payload;
      });
  },
});

export const {
  logout,
  clearError,
  setUser,
  clearAuth,
  dismissOnboarding,
} = authSlice.actions;

export default authSlice.reducer;
